import { GraphNode, GraphEdge, GraphData, NodeType, EdgeType } from '../types/graph';
import type { CloudSaasCorrelation, CloudEntityBlastRadius } from '../services/cloudApi';

function findActivityNode(
  activityNodes: GraphNode[],
  entityEmail: string
): GraphNode | undefined {
  return activityNodes.find(
    (n) =>
      (n.type === 'user' || n.type === 'service_account') &&
      (n.props?.email === entityEmail ||
        n.props?.serviceAccountEmail === entityEmail ||
        n.label === entityEmail ||
        n.id === `user-${entityEmail}` ||
        n.id === `sa-${entityEmail}`)
  );
}

function cloneEntityNode(
  activityNode: GraphNode,
  modePrefix: string,
  extraProps: Record<string, any>
): GraphNode {
  return {
    ...activityNode,
    id: `${modePrefix}-entity-${activityNode.id}`,
    props: {
      ...activityNode.props,
      ...extraProps,

      originalNodeId: activityNode.id,
      originalGraphId: activityNode.props?.graphId,
    },
  };
}

function createFallbackEntityNode(
  entityEmail: string,
  modePrefix: string,
  extraProps: Record<string, any>
): GraphNode {
  return {
    id: `${modePrefix}-entity-${entityEmail}`,
    type: 'user',
    label: entityEmail,
    props: {
      email: entityEmail,
      ...extraProps,
    },
  };
}

export function transformCorrelationsToGraph(
  correlations: CloudSaasCorrelation[],
  activityNodes: GraphNode[]
): GraphData {
  const nodes: GraphNode[] = [];
  const edges: GraphEdge[] = [];
  const nodeIds = new Set<string>();
  let edgeId = 0;

  correlations.forEach((corr) => {
    const correlationMap = corr.correlation || {};

    Object.entries(correlationMap).forEach(([entity, secretMap]) => {
      // Find matching activity node for this entity
      const activityNode = findActivityNode(activityNodes, entity);
      const entityId = activityNode
        ? `corr-entity-${activityNode.id}`
        : `corr-entity-${entity}`;

      if (!nodeIds.has(entityId)) {
        nodeIds.add(entityId);

        const modeProps = {
          isCorrelationMode: true,
          correlationInfo: corr.info,
          severity: corr.severity,
          status: corr.status,
          correlationId: corr.id,
          saasType: corr.info?.saasType,
        };

        if (activityNode) {
          nodes.push(cloneEntityNode(activityNode, 'corr', modeProps));
        } else {
          nodes.push(
            createFallbackEntityNode(entity, 'corr', {
              ...modeProps,
              cloudProvider: corr.cloudType || 'GCP',
              cloudType: corr.cloudType || 'GCP',
            })
          );
        }
      }

      Object.entries(secretMap).forEach(([secretName, saasResources]) => {
        // Create secret node
        const secretId = `corr-secret-${corr.id}-${secretName}`;
        if (!nodeIds.has(secretId)) {
          nodeIds.add(secretId);
          nodes.push({
            id: secretId,
            type: 'secret',
            label: secretName,
            props: {
              isCorrelationMode: true,
              correlationInfo: corr.info,
              severity: corr.severity,
              correlationId: corr.id,
            },
          });
        }

        // Edge: entity → secret
        edges.push({
          id: `corr-edge-${edgeId++}`,
          source: entityId,
          target: secretId,
          type: 'uses',
        });

        // Create SaaS app node
        const saasType = corr.info?.saasType || 'GITHUB';
        const saasAppId = `corr-saas-${corr.id}-${saasType}`;
        if (!nodeIds.has(saasAppId)) {
          nodeIds.add(saasAppId);
          nodes.push({
            id: saasAppId,
            type: 'token',
            label: saasType,
            props: {
              isCorrelationMode: true,
              saasType,
              correlationInfo: corr.info,
              severity: corr.severity,
              correlationId: corr.id,
            },
          });
        }

        // Edge: secret → saas app
        edges.push({
          id: `corr-edge-${edgeId++}`,
          source: secretId,
          target: saasAppId,
          type: 'uses',
        });

        // Create SaaS resource nodes (repos)
        (saasResources || []).forEach((resource) => {
          const resourceId = `corr-resource-${corr.id}-${resource}`;
          if (!nodeIds.has(resourceId)) {
            nodeIds.add(resourceId);
            nodes.push({
              id: resourceId,
              type: 'resource',
              label: resource,
              props: {
                isCorrelationMode: true,
                resourceType: 'repository',
                saasType,
                correlationInfo: corr.info,
                severity: corr.severity,
                correlationId: corr.id,
              },
            });
          }

          // Edge: saas app → resource
          edges.push({
            id: `corr-edge-${edgeId++}`,
            source: saasAppId,
            target: resourceId,
            type: 'acts_on',
          });
        });
      });
    });
  });

  return { nodes, edges, stats: { users: nodes.filter((n) => n.type === 'user' || n.type === 'service_account').length } };
}

const BLAST_NODE_TYPES: Record<string, NodeType> = {
  ROLE: 'role',
  IMPACT: 'capability',
  ACCESS: 'capability',
  RESOURCE: 'resource',
  RESOURCE_GROUP: 'resource_group',
};

const BLAST_EDGE_TYPES: Record<string, EdgeType> = {
  ASSIGNED_ROLE: 'has_permission',
  GRANTS: 'has_permission',
  HAS_ACCESS: 'has_permission',
  REACHES: 'acts_on',
};

export function transformBlastRadiusToGraph(
  blastRadiusData: CloudEntityBlastRadius[],
  activityNodes: GraphNode[]
): GraphData {
  const nodes: GraphNode[] = [];
  const edges: GraphEdge[] = [];
  const nodeIds = new Set<string>();
  let edgeId = 0;

  blastRadiusData.forEach((br) => {
    const graph = br.blastRadius || {};
    const isStructuredGraph = Number(graph.schemaVersion) >= 3 && Array.isArray(graph.nodes) && Array.isArray(graph.edges);

    if (isStructuredGraph) {
      const blastNodes = (graph.nodes || []).filter((node) => !!BLAST_NODE_TYPES[node.type]);
      const blastEdges = (graph.edges || []).filter((edge) => !!BLAST_EDGE_TYPES[edge.type]);
      const identity = graph.identity || {};
      const entity = identity.email || identity.id || br.entity;
      const graphId = `blast-${br.id}`;
      const activityNode = findActivityNode(activityNodes, entity);
      const entityId = activityNode ? `blast-entity-${activityNode.id}` : `blast-entity-${entity}`;
      const modeProps = {
        isBlastRadiusMode: true,
        isBlastRadiusNode: true,
        blastRadiusNodeKind: 'IDENTITY',
        blastRadiusInfo: br.info,
        blastRadiusId: br.id,
        graphId,
        severity: br.info?.risk?.severity,
        riskScore: br.info?.risk?.score,
        email: entity,
        entityType: identity.entityType,
        projectId: identity.projectId,
      };

      if (!nodeIds.has(entityId)) {
        nodeIds.add(entityId);
        if (activityNode) {
          const cloned = cloneEntityNode(activityNode, 'blast', modeProps);
          nodes.push({ ...cloned, risk_score: br.info?.risk?.score ?? cloned.risk_score });
        } else {
          const fallback = createFallbackEntityNode(entity, 'blast', {
            ...modeProps,
            cloudProvider: 'GCP',
            cloudType: 'GCP',
          });
          nodes.push({
            ...fallback,
            type: identity.entityType === 'SERVICE_ACCOUNT' ? 'service_account' : 'user',
            label: identity.label || entity,
            provider: 'gcp',
            risk_score: br.info?.risk?.score,
          });
        }
      }

      const internalIds = new Map<string, string>([['identity', entityId]]);
      blastNodes.forEach((node) => {
        const nodeId = `blast-node-${br.id}-${node.id.replace(/[^a-zA-Z0-9._-]+/g, '-')}`;
        internalIds.set(node.id, nodeId);
        if (nodeIds.has(nodeId)) return;
        nodeIds.add(nodeId);

        const properties = node.properties || {};
        nodes.push({
          id: nodeId,
          type: BLAST_NODE_TYPES[node.type],
          label: node.label,
          provider: 'gcp',
          risk_score: typeof properties.riskScore === 'number' ? properties.riskScore : undefined,
          props: {
            ...properties,
            isBlastRadiusMode: true,
            isBlastRadiusNode: true,
            isBlastRadiusCircular: true,
            blastRadiusNodeKind: node.type,
            blastRadiusInfo: br.info,
            blastRadiusId: br.id,
            graphId,
            severity: properties.riskSeverity || properties.severity || br.info?.risk?.severity,
          },
        });
      });

      blastEdges.forEach((edge) => {
        const source = internalIds.get(edge.source);
        const target = internalIds.get(edge.target);
        if (!source || !target) return;
        edges.push({
          id: `blast-edge-${br.id}-${edgeId++}`,
          source,
          target,
          type: BLAST_EDGE_TYPES[edge.type],
          props: {
            ...(edge.properties || {}),
            graphId,
            blastRadiusEdgeType: edge.type,
          },
        });
      });
      return;
    }

    const blastMap = graph as Record<string, Record<string, string[]>>;

    Object.entries(blastMap).forEach(([entity, secretMap]) => {
      const activityNode = findActivityNode(activityNodes, entity);
      const entityId = activityNode
        ? `blast-entity-${activityNode.id}`
        : `blast-entity-${entity}`;

      if (!nodeIds.has(entityId)) {
        nodeIds.add(entityId);

        const modeProps = {
          isBlastRadiusMode: true,
          isBlastRadiusNode: true,
          blastRadiusInfo: br.info,
          blastRadiusId: br.id,
          graphId: `blast-${br.id}`,
          severity: br.info?.risk?.severity,
        };

        if (activityNode) {
          nodes.push(cloneEntityNode(activityNode, 'blast', modeProps));
        } else {
          nodes.push(
            createFallbackEntityNode(entity, 'blast', {
              ...modeProps,
              cloudProvider: 'GCP',
              cloudType: 'GCP',
            })
          );
        }
      }

      Object.entries(secretMap).forEach(([secretName, saasResources]) => {
        const secretId = `blast-secret-${br.id}-${secretName}`;
        if (!nodeIds.has(secretId)) {
          nodeIds.add(secretId);
          nodes.push({
            id: secretId,
            type: 'secret',
            label: secretName,
            props: {
              isBlastRadiusMode: true,
              isBlastRadiusNode: true,
              isBlastRadiusCircular: true,
              blastRadiusInfo: br.info,
              blastRadiusId: br.id,
              graphId: `blast-${br.id}`,
            },
          });
        }

        edges.push({
          id: `blast-edge-${edgeId++}`,
          source: entityId,
          target: secretId,
          type: 'uses',
        });

        const saasType = br.info?.reachableSaas?.saasType || 'GITHUB';
        const saasAppId = `blast-saas-${br.id}-${saasType}`;
        if (!nodeIds.has(saasAppId)) {
          nodeIds.add(saasAppId);
          nodes.push({
            id: saasAppId,
            type: 'token',
            label: saasType,
            props: {
              isBlastRadiusMode: true,
              isBlastRadiusNode: true,
              isBlastRadiusCircular: true,
              saasType,
              blastRadiusInfo: br.info,
              blastRadiusId: br.id,
              graphId: `blast-${br.id}`,
            },
          });
        }

        edges.push({
          id: `blast-edge-${edgeId++}`,
          source: secretId,
          target: saasAppId,
          type: 'uses',
        });

        (saasResources || []).forEach((resource) => {
          const resourceId = `blast-resource-${br.id}-${resource}`;
          if (!nodeIds.has(resourceId)) {
            nodeIds.add(resourceId);
            nodes.push({
              id: resourceId,
              type: 'resource',
              label: resource,
              props: {
                isBlastRadiusMode: true,
                isBlastRadiusNode: true,
                isBlastRadiusCircular: true,
                resourceType: 'repository',
                saasType,
                blastRadiusInfo: br.info,
                blastRadiusId: br.id,
                graphId: `blast-${br.id}`,
              },
            });
          }

          edges.push({
            id: `blast-edge-${edgeId++}`,
            source: saasAppId,
            target: resourceId,
            type: 'acts_on',
          });
        });
      });
    });
  });

  return { nodes, edges, stats: { users: nodes.filter((n) => n.type === 'user' || n.type === 'service_account').length } };
}
